import React, { useCallback, useEffect, useState } from "react";
import { Lock, QrCode, RefreshCw, ScanLine, User } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

function defaultRouteForRole(role) {
  if (role === "nexo") return "/nexo";
  if (role === "secret") return "/x-portal";
  return "/";
}

function clearLoginState() {
  localStorage.removeItem("ct_login_token");
  localStorage.removeItem("ct_login_user");
  localStorage.removeItem("ct_admin_auth");
  localStorage.removeItem("ct_2fa_token");
  localStorage.removeItem("ct_2fa_user");
  localStorage.removeItem("ct_email_otp_token");
}

export default function ScannerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(location.state?.mode || "scan");
  const [challenge, setChallenge] = useState(null);
  const [scanStatus, setScanStatus] = useState("Creating secure QR...");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("ct_login_token");
    if (!token) return undefined;

    let cancelled = false;

    const verifyExistingLogin = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/login/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;

        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          if (data.success && data.user) {
            localStorage.setItem("ct_login_user", JSON.stringify(data.user));
            navigate(defaultRouteForRole(data.user.role), { replace: true });
            return;
          }
        }

        if (response.status === 401) {
          clearLoginState();
        }
      } catch {
        // Stay on login if the server is temporarily unreachable.
      }
    };

    verifyExistingLogin();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const continueToTwoFactor = useCallback((data) => {
    localStorage.setItem("ct_2fa_token", data.tempToken);
    localStorage.setItem("ct_2fa_user", JSON.stringify(data.user));
    navigate(data.setupRequired ? "/two-factor-setup" : "/two-factor", { replace: true });
  }, [navigate]);

  const createChallenge = useCallback(async () => {
    setChallenge(null);
    setScanStatus("Creating secure QR...");
    setError("");
    try {
      const response = await fetch(`${API_BASE}/api/qr-login/challenge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin: window.location.origin }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "QR creation failed");
      setChallenge(data);
      setScanStatus("Scan with your logged-in Nexovate mobile app");
    } catch (nextError) {
      setScanStatus(nextError.message);
    }
  }, []);

  useEffect(() => {
    if (mode === "scan") createChallenge();
  }, [createChallenge, mode]);

  useEffect(() => {
    if (!challenge?.challengeId || mode !== "scan") return undefined;

    const poll = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/qr-login/status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            challengeId: challenge.challengeId,
            browserToken: challenge.browserToken,
          }),
        });
        const data = await response.json();
        if (response.status === 410) {
          setScanStatus("QR expired. Generate a new QR.");
          return;
        }
        if (!response.ok || !data.success) throw new Error(data.error || "QR status failed");
        if (data.status === "approved") {
          if (data.requiresTwoFactor) {
            setScanStatus("Mobile approved. Opening two-factor verification...");
            continueToTwoFactor(data);
            return;
          }

          if (data.requiresEmailOtp) {
            localStorage.setItem("ct_email_otp_token", data.emailOtpToken);
            localStorage.setItem("ct_2fa_user", JSON.stringify(data.user));
            navigate("/email-otp", { replace: true });
            return;
          }

          localStorage.setItem("ct_login_token", data.token);
          localStorage.setItem("ct_login_user", JSON.stringify(data.user));
          navigate("/", { replace: true });
        }
      } catch (nextError) {
        setScanStatus(nextError.message);
      }
    };

    poll();
    const timer = setInterval(poll, 1500);
    return () => clearInterval(timer);
  }, [challenge, continueToTwoFactor, mode]);

  const passwordLogin = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/login/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Invalid username or password");
      if (data.requiresTwoFactor) {
        continueToTwoFactor(data);
        return;
      }
      localStorage.setItem("ct_login_token", data.token);
      localStorage.setItem("ct_login_user", JSON.stringify(data.user));
      navigate("/", { replace: true });
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-4" style={{ background: "#020202" }}>
      <section className="w-full max-w-[460px] rounded-xl p-7" style={{ background: "#080704", border: "1px solid rgba(247,201,72,.32)" }}>
        <div className="flex justify-center gap-2 mb-6">
          <button className={mode === "scan" ? "terminal-tab-active px-4 py-2 rounded" : "px-4 py-2 rounded"} onClick={() => setMode("scan")}>
            <ScanLine size={16} className="inline mr-2" />Scan Login
          </button>
          <button className={mode === "password" ? "terminal-tab-active px-4 py-2 rounded" : "px-4 py-2 rounded"} onClick={() => setMode("password")}>
            <Lock size={16} className="inline mr-2" />Password Login
          </button>
        </div>

        {mode === "scan" ? (
          <div className="text-center">
            <QrCode size={34} className="mx-auto mb-3" />
            <h1 className="text-2xl font-bold mb-2">Secure Scanner Login</h1>
            <p className="text-sm mb-5">Approve on an already logged-in mobile device. Authenticator OTP remains mandatory.</p>
            {challenge?.qrDataUrl ? (
              <img src={challenge.qrDataUrl} alt="Nexovate secure login QR" className="mx-auto rounded-lg p-2 mb-4" style={{ width: 260, background: "#fff" }} />
            ) : null}
            <div className="text-sm mb-4">{scanStatus}</div>
            <button className="btn-solid-green px-4 py-2 rounded" onClick={createChallenge}>
              <RefreshCw size={15} className="inline mr-2" />New Secure QR
            </button>
          </div>
        ) : (
          <form onSubmit={passwordLogin}>
            <h1 className="text-2xl font-bold text-center mb-6">Username Login</h1>
            <label className="block text-sm mb-2">Username</label>
            <div className="relative mb-4">
              <User size={17} className="absolute left-3 top-3" />
              <input className="w-full rounded py-3 pl-10 pr-3" value={username} onChange={event => setUsername(event.target.value)} autoComplete="username" />
            </div>
            <label className="block text-sm mb-2">Password</label>
            <div className="relative mb-4">
              <Lock size={17} className="absolute left-3 top-3" />
              <input type="password" className="w-full rounded py-3 pl-10 pr-3" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" />
            </div>
            {error ? <div className="text-red-400 text-sm mb-4">{error}</div> : null}
            <button className="btn-solid-green w-full py-3 rounded" disabled={loading}>
              {loading ? "Signing In..." : "Continue to Two Factor"}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
